import React, { FunctionComponent } from 'react'
import { InlineMessageProps, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Dialog, DialogProps, createStyles, makeStyles, Box } from 'transfix-ui/core'
import { calcGrid } from 'transfix-ui'
import { DialogActions } from './DialogActions'
import { DialogContent } from './DialogContent'
import { DialogTitle } from './DialogTitle'

export interface FullPageOverlayProps {
  className?: string
  children: React.ReactNode
  isOpen: boolean
  onClose: () => void
  title?: React.ReactNode
  footer?: JSX.Element
  /**
   * Inline message will appear in the DialogTitle when fullScreen is true.  Otherwise it appears below the DialogTitle.
   */
  inlineMessageProps?: InlineMessageProps
  onSubmit?: () => void
  onOpen?: () => void
  onScroll?: () => void
  fullScreen?: boolean
  showContentDividers?: boolean
  maxWidth?: DialogProps['maxWidth']
}

type TClassKey = 'paperWidthSm' | 'paperWidthMd' | 'paperWidthLg'

const useFullPageOverlayStyles = makeStyles(() =>
  createStyles<TClassKey, Pick<FullPageOverlayProps, 'fullScreen'>>({
    paperWidthSm: {
      maxWidth: calcGrid(4),
    },
    paperWidthMd: {
      maxWidth: '50%',
    },
    paperWidthLg: {
      maxWidth: calcGrid(2),
    },
  })
)

export const FullPageOverlay: FunctionComponent<FullPageOverlayProps> = ({
  children,
  className,
  footer,
  fullScreen = true,
  inlineMessageProps,
  isOpen,
  maxWidth,
  onClose,
  onOpen,
  onScroll,
  onSubmit,
  showContentDividers = true,
  title,
}) => {
  const classes = useFullPageOverlayStyles({ fullScreen })
  const submitFormHandler = () => {
    if (onSubmit) {
      onSubmit()
    }
  }
  return (
    <form onSubmit={submitFormHandler}>
      <Dialog
        classes={classes}
        TransitionProps={{
          onEnter: onOpen,
        }}
        className={className}
        scroll='paper'
        fullScreen={fullScreen}
        fullWidth
        maxWidth={fullScreen ? false : maxWidth}
        onClose={onClose}
        aria-labelledby='customized-dialog-title'
        open={isOpen}>
        {title && (
          <DialogTitle
            onClose={onClose}
            inlineMessageProps={fullScreen ? inlineMessageProps : undefined}>
            {title}
          </DialogTitle>
        )}
        {inlineMessageProps && !fullScreen && (
          <Box paddingLeft={3} paddingRight={3} paddingBottom={2}>
            <InlineMessage {...inlineMessageProps} />
          </Box>
        )}
        <DialogContent dividers={showContentDividers} onScroll={onScroll}>
          {children}
        </DialogContent>
        {footer && <DialogActions>{footer}</DialogActions>}
      </Dialog>
    </form>
  )
}
